/**
 * OfficerAssignPopover
 *
 * Appears after the user drag-selects (or presses and holds) a time range on a counter row (counter
 * timetable), or taps a single empty cell there. The pivot of CounterAssignPopover: here the row is the
 * counter, so the pick is which officer works it.
 *
 * One step, whatever the range held: put an officer on the counter for the range — on a range someone holds
 * it opens on them, so a change of officer is one pick — or clear it.
 *
 * Start, end and the officer are all editable before anything is saved: a finger-drawn selection is often a
 * slot out, so the two time pickers are the correction, and nothing is written until Save is pressed.
 */

import { useEffect, useRef, useState } from "react";
import { TimeRangeSelect } from "@/components/TimeRangeSelect";
import { PopoverShell } from "@/components/PopoverShell";
import { ActionNote, ActionTabs, PopoverFooter, PopoverHeader } from "@/components/PopoverParts";
import { fmtHhmm, isNarrow } from "@/utils/popoverLayout";
import { CELL_ACTION_LOOK, type CellAction, type CellActionKind } from "@/utils/cellAction";

export interface OfficerOption {
  id: string;
  label: string;
}

interface OfficerAssignPopoverProps {
  startTime: string;
  endTime: string;
  /** Shift the grid is showing — drives the time pickers' option list. */
  shiftType?: string;
  /**
   * Called when the user edits the range. The parent owns start/end (it also
   * repaints the grid highlight from them), so this component stays controlled.
   * Omit to render the range read-only.
   */
  onRangeChange?: (startTime: string, endTime: string) => void;
  anchorX: number;
  anchorY: number;
  officerOptions: OfficerOption[];
  /** Who holds the range now: the picker opens on them, and Clear is offered. */
  currentOfficerId?: string;
  /** Called with what the user chose: an officer for the range, or (when someone holds it) clear. */
  onAction: (action: CellAction) => Promise<void>;
  onCancel: () => void;
  errorMessage?: string;
}

const POPOVER_W = 300;
const POPOVER_H = 300;

export function OfficerAssignPopover({
  startTime,
  endTime,
  shiftType = "morning",
  onRangeChange,
  anchorX,
  anchorY,
  officerOptions,
  currentOfficerId,
  onAction,
  onCancel,
  errorMessage,
}: OfficerAssignPopoverProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const [selectedId, setSelectedId] = useState(currentOfficerId ?? officerOptions[0]?.id ?? "");
  const actions: CellActionKind[] = currentOfficerId ? ["officer", "clear"] : ["officer"];
  const [action, setAction] = useState<CellActionKind>("officer");
  const [busy, setBusy] = useState(false);

  const isMobile = isNarrow();

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  const handleConfirm = async () => {
    if (action === "officer" && !selectedId) return;
    setBusy(true);
    try {
      await onAction(action === "clear" ? { kind: "clear" } : { kind: "officer", officerId: selectedId });
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleConfirm();
    if (e.key === "Escape") onCancel();
  };

  const color = CELL_ACTION_LOOK[action].color;
  const range = `${fmtHhmm(startTime)} – ${fmtHhmm(endTime)}`;
  const verb = action === "clear" ? "Clear" : currentOfficerId ? "Change officer" : "Assign officer";
  const currentLabel = officerOptions.find((o) => o.id === currentOfficerId)?.label ?? currentOfficerId;

  return (
    <PopoverShell anchorX={anchorX} anchorY={anchorY} width={POPOVER_W} approxHeight={POPOVER_H} onCancel={onCancel}>
      <div onKeyDown={handleKey}>
        <PopoverHeader title={`${verb} · ${range}`} color={color} busy={busy} onClose={onCancel} />

        {/* Range pickers — the highlight can be a slot out either side */}
        {onRangeChange && (
          <TimeRangeSelect
            shiftType={shiftType}
            start={startTime}
            end={endTime}
            onChange={onRangeChange}
            isMobile={isMobile}
            accent={action === "clear" ? color : undefined}
            disabled={busy}
          />
        )}

        <ActionTabs actions={actions} value={action} onChange={setAction} isMobile={isMobile} />

        {action === "officer" ? (
          <>
            <label style={{ fontSize: 12, fontWeight: 500, color: "#64748b", display: "block", marginBottom: 6 }}>
              Officer
            </label>
            <select
              ref={selectRef}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                width: "100%",
                padding: isMobile ? "12px 14px" : "10px 12px",
                fontSize: isMobile ? 16 : 15,
                fontWeight: 500,
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                background: "white",
                appearance: "auto",
                marginBottom: 14,
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              {officerOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <ActionNote color={color}>Takes {currentLabel} off this counter for {range}</ActionNote>
        )}

        <PopoverFooter
          action={action}
          busy={busy}
          disabled={action === "officer" && !selectedId}
          error={errorMessage}
          onSave={handleConfirm}
          isMobile={isMobile}
        />
      </div>
    </PopoverShell>
  );
}
